import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Button, ButtonGroup } from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import CICServices from "../../../services/COOPServices/CICServices";
import { toast } from "react-toastify";
import DeleteModal from "../../../Components/Common/DeleteModal/DeleteModal";
import DateFormatter from "../../../Components/Common/DateFromatter";
import Flatpickr from "react-flatpickr";

const CIC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const fetchData = async () => {
    try {
      const fetchedData = await CICServices.get();
      setData(fetchedData.data);
      setFilteredData(fetchedData.data); // Initialize filteredData with all data
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedReport, dateRange]);

  const applyFilters = () => {
    let filtered = [...data];

    // Apply search filter
    if (searchQuery !== "") {
      filtered = filtered.filter((item) =>
        item.submissionNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply reporting type filter
    if (selectedReport) {
      filtered = filtered.filter(
        (item) => item.reportingBasis === selectedReport.value
      );
    }

    // Apply date range filter
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(Date.parse(item.loanMaturityDate));
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    setFilteredData(filtered);
  };

  const handleDelete = async () => {
    try {
      const temp = data.filter((item) => item.id !== selectedDeleteItem.id);
      await CICServices.delete(selectedDeleteItem.id);
      console.log("Item deleted successfully");

      setData(temp); // Update the data state
      setFilteredData(temp); // Update filteredData if needed

      setSelectedDeleteItem(null);
      toggleDeleteModal();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete CIC Data.");
    }
  };

  const statusColors = {
    DueIntrest: "bg-primary",
    DueInstallment: "bg-warning",
    LoanOverDue: "bg-danger",
  };

  const statusColors1 = {
    Individual: "bg-primary",
    Institutional: "bg-warning",
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row) => row.sn,
      sortable: true,
      width: "75px",
    },
    {
      name: "Submission No.",
      selector: (row) => row.submissionNumber,
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name || "No data available",
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => (
        <span
          className={`badge ${
            statusColors1[row.defaulterType] || ""
          }-subtle text-uppercase text-dark`}
        >
          {row.defaulterType === "Individual"
            ? "Individual"
            : row.defaulterType === "Institutional"
            ? "Institutional"
            : "Unknown"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Loan amount",
      selector: (row) => row.loanAmount || "No data available",
      sortable: true,
    },
    {
      name: "Loan Maturity",
      selector: (row) =>
        DateFormatter({ dateString: row.loanMaturityDate }) ||
        "No data available",
      sortable: true,
    },
    {
      name: "Reporting Type",
      selector: (row) => (
        <span
          className={`badge ${
            statusColors[row.reportingBasis] || ""
          }-subtle text-uppercase text-dark`}
        >
          {row.reportingBasis === "DueIntrest"
            ? "Due Interest"
            : row.reportingBasis === "DueInstallment"
            ? "Due Installment"
            : row.reportingBasis === "LoanOverDue"
            ? "Loan Overdue"
            : "Unknown"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => row.action,
      right: true,
    },
  ];

  const dataRows = filteredData?.map((item, index) => ({
    sn: index + 1,
    submissionNumber:
      item.defaulterType === "Institutional" ? (
        <Link to={`/cic/institution-defaulters-details/${item.id}`}>
          {item.submissionNumber}
        </Link>
      ) : (
        <Link to={`/cic/defaulters-details/${item.id}`}>
          {item.submissionNumber}
        </Link>
      ),
    name: item.name,
    defaulterType: item.defaulterType,
    loanAmount: item.loanAmount,
    loanMaturityDate: item.loanMaturityDate,
    reportingBasis: item.reportingBasis,
    action: (
      <ButtonGroup size="sm">
        <div className="d-flex gap-1">
          <Link
            to={`/cic/edit/${item.id}`}
            state={{ defaulterType: item.defaulterType }}
          >
            <Button
              color="rounded-circle btn btn-soft-warning"
              className="rounded-circle btn-sm gap-1"
            >
              <i className="ri-edit-line"></i>
            </Button>
          </Link>
          <Button
            className="rounded-circle btn btn-soft-danger btn-sm gap-1"
            color="danger"
            onClick={() => {
              setSelectedDeleteItem(item);
              toggleDeleteModal();
            }}
          >
            <i className="ri-delete-bin-2-line"></i>
          </Button>
        </div>
      </ButtonGroup>
    ),
  }));

  const handleDateChange = ({ startDate, endDate }) => {
    if (endDate && endDate < startDate) {
      toast.error("End date cannot be before start date");
      return;
    }
    setDateRange({ startDate, endDate });
  };

  return (
    <Container fluid>
      <div className="page-content">
        <Row>
          <Col xs={6} className="mb-0">
            <h5>
              <span className="px-1">CIC</span>
            </h5>
          </Col>
          <Col xs={6} className="text-end">
            <Button
              color="success"
              onClick={() => {
                navigate("/cic/add-new-defaulters");
              }}
              className="float-end"
            >
              <i className="ri-add-line align-bottom me-1"></i>
              Add New Defaulters
            </Button>
          </Col>
        </Row>
        <Row className="gap-0 mb-2">
          <Col xs={3}>
            <div className="search-box ">
              <input
                type="text"
                className="form-control search"
                placeholder="Search by submission no..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </Col>
          <Col xs={3}>
            <div className=" ">
              {/* <DatePicker onDateChange={handleDateChange} /> */}
              <Flatpickr
                className="form-control"
                id="datepicker-publish-input"
                placeholder="Select a range of date"
                options={{
                  altInput: true,
                  altFormat: "F j, Y",
                  mode: "range",
                  dateFormat: "d.m.y",
                }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
              />
            </div>
          </Col>
          <Col xs={3}>
            <div className="search-box ">
              <Select
                isClearable={true}
                placeholder="Select by reporting type"
                options={[
                  { value: "DueIntrest", label: "Due Interest" },
                  { value: "DueInstallment", label: "Due Installment" },
                  { value: "LoanOverDue", label: "Loan Overdue" },
                ]}
                value={selectedReport}
                onChange={setSelectedReport}
              />
            </div>
          </Col>
        </Row>
        <DataTable
          responsive
          striped
          pagination
          highlightOnHover
          pointerOnHover
          persistTableHead
          customStyles={customStyles}
          columns={columns}
          data={dataRows}
        />
        {selectedDeleteItem && (
          <DeleteModal
            deleteTerm={selectedDeleteItem.name}
            toggle={toggleDeleteModal}
            isOpen={deleteModal}
            handleDelete={handleDelete}
          />
        )}
      </div>
    </Container>
  );
};

export default CIC;
